import { OutAdminProductDTO, OutAdminProductDTOCreatedByEnum } from "@app/api/generated";

const MS_PER_DAY = 86_400_000;
/** A page counts as "almost offline / almost deleted" within this many days of its lifecycle date. */
const NEAR_THRESHOLD_DAYS = 5;

/** Whole days from now until `date` (negative = overdue). */
export const daysUntil = (date: Date): number => Math.ceil((new Date(date).getTime() - Date.now()) / MS_PER_DAY);

const isNear = (date?: Date | null): boolean => date != null && daysUntil(date) <= NEAR_THRESHOLD_DAYS;

type ProductLifecycle = Pick<OutAdminProductDTO, "live" | "createdBy" | "offlineAt" | "deleteAt">;

export interface ILabelVariant {
  /** `LabelComponent` colour variant. */
  type: "live" | "offline" | "warning" | "failed";
  /** i18n key for the label text. */
  labelId: string;
}

/** The actual page status — maps to the `live` boolean, so it stays sortable/filterable. */
export const getProductStatus = (product: Pick<OutAdminProductDTO, "live">): ILabelVariant =>
  product.live
    ? { type: "live", labelId: "products.status.live" }
    : { type: "offline", labelId: "products.status.offline" };

/**
 * Lifecycle warning shown next to "grace since": "Bijna offline" for a live page nearing its
 * offline date, "Bijna verwijderen" for an automatic offline page nearing deletion. Null otherwise.
 */
export const getLifecycleWarning = (product: ProductLifecycle): ILabelVariant | null => {
  if (product.live) {
    return isNear(product.offlineAt) ? { type: "warning", labelId: "products.status.almostOffline" } : null;
  }
  if (product.createdBy === OutAdminProductDTOCreatedByEnum.API && isNear(product.deleteAt)) {
    return { type: "failed", labelId: "products.status.almostDeleted" };
  }
  return null;
};
